import React, { useEffect, useMemo, useState } from "react";
import {
  getMyStaff, listSubjects, listClasses, listStaff, TEACHER_DEPARTMENT, DAYS,
  getMyBusyWeeks, listTimetableRuns, getTimetableRun, getPeriodTimes, getVocationalSchedule,
} from "./happyschoolApi";

const khoiOf = (className) => (String(className).match(/^(\d{1,2})A/) || [])[1] || "";
const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/gi, "d").toLowerCase().replace(/[^a-z0-9]/g, "");
const stripHonorific = (s) => String(s || "").replace(/^\s*(cô giáo|thầy giáo|cô|thầy)\s+/i, "").trim();
const NGHE_MAX_IDX = 3; // học nghề mỗi buổi chỉ học tiết 1-4, không có tiết 5

function buildSessions(pt) {
  let cur = 0;
  return (pt && pt.sessions ? pt.sessions : []).map((s) => {
    const periods = (s.periods || []).map((time, i) => ({ global: cur + i, idx: i, time }));
    cur += (s.periods || []).length;
    return { key: s.key, label: s.label, periods };
  });
}
// Tách chuỗi "Ngày học" của nghề -> danh sách {d, s} (s: 0=sáng,1=chiều).
function ngheSlots(dayStr) {
  const str = String(dayStr).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const out = [];
  const re = /(sang|chieu|ngay)\s+thu\s+(\d)/g;
  let m;
  while ((m = re.exec(str))) {
    const dow = Number(m[2]);
    if (dow < 2 || dow > 7) continue;
    const d = dow - 2;
    (m[1] === "sang" ? [0] : m[1] === "chieu" ? [1] : [0, 1]).forEach((s) => out.push({ d, s }));
  }
  return out;
}

// GV "dạy thay" = người đang được phân công (Phân công chuyên môn thật, không phải
// bản nháp PCCM), gồm 2 nhóm, LUÔN xếp nhóm 1 lên trước nhóm 2:
//  1. "Cùng bộ môn": cùng môn + cùng khối với ít nhất 1 lớp GV hiện tại đang dạy.
//  2. "Cùng lớp": dạy môn khác nhưng CÙNG LỚP với đúng lớp đang chọn ở combobox
//     Lớp (không phải bất kỳ lớp nào của GV hiện tại) — vì cần thay ĐÚNG buổi
//     của LỚP ĐÓ, biết ai đang có mặt ở lớp đó là hợp lý nhất để nhờ trông/dạy thay.
// myClasses: đúng các (lớp, môn) mà GV hiện tại đang dạy — để chọn nhanh 1 lớp
// rồi tự tìm ra đúng buổi/tiết của lớp đó trong TKB, khỏi phải dò tay Thứ/Tiết.
function computeSubjectMatches(myStaffId, classes, teachers, subjectName) {
  const myPairs = new Set(); // "khoi|subjectId"
  const myClasses = [];
  classes.forEach((cls) => {
    const khoi = khoiOf(cls.name);
    (cls.subjects || []).forEach((s) => {
      if (s.assignedTeacherId !== myStaffId) return;
      if (khoi) myPairs.add(khoi + "|" + s.subjectId);
      myClasses.push({ classId: cls.id, className: cls.name, subjectId: s.subjectId, subjectName: subjectName[s.subjectId] || s.subjectId });
    });
  });
  myClasses.sort((a, b) => a.className.localeCompare(b.className, "vi", { numeric: true }) || a.subjectName.localeCompare(b.subjectName, "vi"));
  if (!myPairs.size) return { subjectMatches: [], myClasses };

  const byTeacher = {};
  classes.forEach((cls) => {
    const khoi = khoiOf(cls.name);
    (cls.subjects || []).forEach((s) => {
      if (!s.assignedTeacherId || s.assignedTeacherId === myStaffId) return;
      if (!khoi || !myPairs.has(khoi + "|" + s.subjectId)) return;
      const sName = subjectName[s.subjectId] || s.subjectId;
      (byTeacher[s.assignedTeacherId] = byTeacher[s.assignedTeacherId] || new Set()).add(`${sName} - Khối ${khoi}`);
    });
  });

  const subjectMatches = Object.entries(byTeacher)
    .map(([staffId, matches]) => {
      const t = teachers.find((x) => x.id === staffId);
      return { staffId, name: (t && t.name) || staffId, phone: (t && t.phone) || "", matches: Array.from(matches).sort() };
    })
    .filter((t) => t.phone)
    .sort((a, b) => a.name.localeCompare(b.name, "vi"));
  return { subjectMatches, myClasses };
}

const HappySchoolSubstituteTeachers = ({ schoolId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [state, setState] = useState({ status: "ok", subjectMatches: [], myClasses: [], myStaffId: "", classesRaw: [], subjectNameMap: {}, teacherById: {} });

  const [weeks, setWeeks] = useState([]);
  const [weekId, setWeekId] = useState("");
  const [runId, setRunId] = useState("");
  const [run, setRun] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [voc, setVoc] = useState({ rows: [] });
  const [teacherBusy, setTeacherBusy] = useState({}); // staffId -> mảng key "day_period" / "day_S" / "day_C" đã khai báo bận
  const [slotLoading, setSlotLoading] = useState(false);

  const [classPick, setClassPick] = useState(""); // "classId|subjectId" — lớp GV đang dạy, chọn để tự tìm buổi/tiết
  const [day, setDay] = useState("");
  const [si, setSi] = useState("");
  const [tietIdx, setTietIdx] = useState("");
  // Mở màn hình lên KHÔNG tự tìm gì cả — phải điền đủ Lớp + Thứ + Buổi + Tiết
  // rồi bấm "Tìm kiếm" mới hiện kết quả; thiếu trường nào báo đỏ trường đó.
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState("");

  // Đổi bất kỳ ô lọc nào -> phải bấm Tìm kiếm lại, ẩn kết quả cũ đi.
  useEffect(() => { setSubmitted(false); setFormError(""); }, [weekId, classPick, day, si, tietIdx]);

  // Tải danh sách GV dạy thay + danh sách tuần TKB.
  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    Promise.all([getMyStaff(schoolId), listSubjects(schoolId), listClasses(schoolId), listStaff(schoolId, { department: TEACHER_DEPARTMENT }), getMyBusyWeeks(schoolId)])
      .then(([d, subs, classes, teachers, ws]) => {
        if (d && d.needVerify) return setState({ status: "needVerify" });
        if (!d || !d.staffId) return setState({ status: "notLinked" });
        const subjectNameMap = Object.fromEntries((subs || []).map((s) => [s.id, s.name]));
        const teacherById = Object.fromEntries((teachers || []).map((t) => [t.id, { name: t.name, phone: t.phone || "" }]));
        const { subjectMatches, myClasses } = computeSubjectMatches(d.staffId, classes || [], teachers || [], subjectNameMap);
        setState({ status: "ok", subjectMatches, myClasses, myStaffId: d.staffId, classesRaw: classes || [], subjectNameMap, teacherById });
        setWeeks((ws || []).slice().sort((a, b) => (b.startDate || "").localeCompare(a.startDate || "")));
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [schoolId]);

  useEffect(() => {
    if (weeks.length && !weekId) setWeekId(weeks[0].id);
  }, [weeks]); // eslint-disable-line

  // Chọn tuần -> lấy bản TKB mới nhất đã xếp xong của tuần đó + lịch bận GV đã khai báo cho tuần đó.
  useEffect(() => {
    if (!schoolId || !weekId) return;
    setRunId("");
    setRun(null);
    listTimetableRuns(schoolId, weekId)
      .then((runs) => {
        const latestDone = (runs || []).find((r) => r.status === "done");
        if (latestDone) setRunId(latestDone.id);
      })
      .catch(() => {});
    listStaff(schoolId, { department: TEACHER_DEPARTMENT, weekId })
      .then((teachers) => setTeacherBusy(Object.fromEntries((teachers || []).map((t) => [t.id, t.busy || []]))))
      .catch(() => {});
  }, [schoolId, weekId]);

  // Có bản TKB -> tải grid + giờ học + lịch nghề để tính trống/bận.
  useEffect(() => {
    if (!schoolId || !runId) return;
    setSlotLoading(true);
    Promise.all([getTimetableRun(schoolId, runId), getPeriodTimes(schoolId), weekId ? getVocationalSchedule(schoolId, weekId) : Promise.resolve({ rows: [] })])
      .then(([r, pt, v]) => {
        setRun(r);
        setSessions(buildSessions(pt));
        setVoc(v || { rows: [] });
      })
      .catch((e) => setError(e.message))
      .finally(() => setSlotLoading(false));
  }, [schoolId, runId, weekId]);

  const week = weeks.find((w) => w.id === weekId);
  const grid = (run && run.grid) || {};
  const ngheByTeacherName = useMemo(() => {
    const map = {};
    (voc.rows || []).forEach((r) => {
      if (!r.teacher) return;
      const key = norm(stripHonorific(r.teacher));
      ngheSlots(r.day).forEach(({ d, s }) => {
        (map[`${key}|${d}|${s}`] = map[`${key}|${d}|${s}`] || []).push(1);
      });
    });
    return map;
  }, [voc]);

  // Các buổi/tiết trong tuần mà lớp+môn đang chọn thực sự có GV hiện tại dạy
  // (đọc thẳng từ TKB) — chọn lớp là tự nhảy tới đúng chỗ, khỏi dò tay Thứ/Tiết.
  const myClassSlots = useMemo(() => {
    if (!classPick || !state.myStaffId) return [];
    const [cid, sid] = classPick.split("|");
    const out = [];
    Object.entries(grid).forEach(([key, val]) => {
      if (!val || val[0] !== sid || val[1] !== state.myStaffId) return;
      const [gcid, gday, gp] = key.split("|");
      if (gcid !== cid) return;
      const gNum = Number(gp);
      const siIdx = sessions.findIndex((s) => s.periods.some((p) => p.global === gNum));
      if (siIdx < 0) return;
      const p = sessions[siIdx].periods.find((pp) => pp.global === gNum);
      out.push({ day: Number(gday), si: siIdx, tietIdx: p.idx, global: gNum, time: p.time });
    });
    out.sort((a, b) => a.day - b.day || a.global - b.global);
    return out;
  }, [classPick, grid, sessions, state.myStaffId]);

  // Chỉ 1 buổi/tiết khớp -> tự chọn luôn; nhiều hơn thì hiện các ô để bấm chọn.
  useEffect(() => {
    if (!classPick || myClassSlots.length !== 1) return;
    const s0 = myClassSlots[0];
    setDay(String(s0.day));
    setSi(String(s0.si));
    setTietIdx(String(s0.tietIdx));
  }, [classPick, myClassSlots]);

  const g = day !== "" && si !== "" && tietIdx !== "" ? ((sessions[Number(si)] || {}).periods || []).find((p) => p.idx === Number(tietIdx)) : null;
  const slotChosen = day !== "" && si !== "" && tietIdx !== "" && g;

  // Bấm "Tìm kiếm": thiếu trường nào báo đỏ đúng trường đó, đủ mới hiện kết quả.
  const runSearch = () => {
    const missing = [];
    if (!classPick) missing.push("Lớp");
    if (day === "") missing.push("Thứ");
    if (si === "") missing.push("Buổi");
    if (tietIdx === "") missing.push("Tiết");
    if (missing.length) {
      setFormError(`Chưa chọn: ${missing.join(", ")}.`);
      setSubmitted(false);
      return;
    }
    setFormError("");
    setSubmitted(true);
  };

  // GV busy tại tiết đã chọn: có tiết văn hoá trong grid HOẶC có lịch dạy nghề (theo tên).
  const busyStaffIds = useMemo(() => {
    if (!slotChosen) return new Set();
    const ids = new Set();
    Object.entries(grid).forEach(([key, val]) => {
      if (!val) return;
      const parts = key.split("|");
      const kDay = Number(parts[1]);
      const kG = Number(parts[2]);
      if (kDay === Number(day) && kG === g.global) ids.add(val[1]);
    });
    return ids;
  }, [grid, day, g, slotChosen]);

  const isNgheBusy = (staffName) => {
    if (!slotChosen || g.idx > NGHE_MAX_IDX) return false;
    return !!(ngheByTeacherName[`${norm(staffName)}|${day}|${si}`]);
  };

  // Khớp đúng cách server tính bận (busyKeyToPairs trong timetable.js): key
  // "day_period" (tiết cụ thể) hoặc "day_S"/"day_C" (cả buổi).
  const isDeclaredBusy = (staffId) => {
    if (!slotChosen) return false;
    const sesKey = Number(si) === 0 ? "S" : "C";
    return (teacherBusy[staffId] || []).some((key) => {
      const [dStr, rest] = String(key).split("_");
      if (Number(dStr) !== Number(day)) return false;
      return rest === sesKey || Number(rest) === g.global;
    });
  };

  // Gộp 2 nhóm GV dạy thay: "cùng bộ môn" (đã tính sẵn, không đổi theo lớp
  // chọn) + "cùng lớp" (chỉ tính theo đúng lớp đang chọn ở combobox Lớp).
  // Nhóm cùng bộ môn LUÔN xếp trước nhóm cùng lớp.
  const combinedMatches = useMemo(() => {
    const byId = {};
    state.subjectMatches.forEach((t) => {
      byId[t.staffId] = { staffId: t.staffId, name: t.name, phone: t.phone, matches: [...t.matches], hasSubject: true };
    });
    if (classPick) {
      const [cid] = classPick.split("|");
      const cls = state.classesRaw.find((c) => c.id === cid);
      if (cls) {
        (cls.subjects || []).forEach((s) => {
          if (!s.assignedTeacherId || s.assignedTeacherId === state.myStaffId) return;
          const sName = state.subjectNameMap[s.subjectId] || s.subjectId;
          const text = `${sName} - Lớp ${cls.name} (dạy cùng lớp)`;
          if (byId[s.assignedTeacherId]) {
            if (!byId[s.assignedTeacherId].matches.includes(text)) byId[s.assignedTeacherId].matches.push(text);
          } else {
            const t = state.teacherById[s.assignedTeacherId];
            if (t && t.phone) {
              byId[s.assignedTeacherId] = { staffId: s.assignedTeacherId, name: t.name, phone: t.phone, matches: [text], hasSubject: false };
            }
          }
        });
      }
    }
    return Object.values(byId);
  }, [state, classPick]);

  if (loading) return <div style={{ padding: 24 }}>Đang tải...</div>;
  if (error) return <div style={{ padding: 24, color: "#dc2626" }}>{error}</div>;
  if (state.status === "needVerify") return <div style={{ padding: 24, color: "#92400e" }}>🔒 Bạn cần xác thực thông tin trước.</div>;
  if (state.status === "notLinked") return <div style={{ padding: 24, color: "#b45309" }}>Tài khoản chưa liên kết hồ sơ nhân sự.</div>;

  const withBusy = combinedMatches
    .map((t) => ({
      ...t,
      busy: slotChosen ? (busyStaffIds.has(t.staffId) || isNgheBusy(t.name) || isDeclaredBusy(t.staffId)) : null,
    }))
    .sort((a, b) => {
      if (a.hasSubject !== b.hasSubject) return a.hasSubject ? -1 : 1;
      if (slotChosen && a.busy !== b.busy) return a.busy ? 1 : -1;
      return a.name.localeCompare(b.name, "vi");
    });

  return (
    <div style={{ maxWidth: 700, margin: "0 auto" }}>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Các thầy cô đang dạy cùng bộ môn, cùng khối (theo Phân công chuyên môn) — khi bạn bận, có thể gọi điện nhờ dạy thay.
      </p>

      {weeks.length === 0 ? (
        <p style={{ color: "#b45309", fontSize: 13 }}>Chưa có tuần TKB nào đang mở.</p>
      ) : (
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#fff", marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#4b5563", marginBottom: 8 }}>Chọn đủ Tuần, Lớp, Thứ, Buổi, Tiết rồi bấm Tìm kiếm</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <select value={weekId} onChange={(e) => { setWeekId(e.target.value); setClassPick(""); setDay(""); setSi(""); setTietIdx(""); }} style={sel}>
              {weeks.map((w) => (
                <option key={w.id} value={w.id}>{(w.startDate || "?") + " → " + (w.endDate || "?")}</option>
              ))}
            </select>
            <select
              value={classPick}
              onChange={(e) => { setClassPick(e.target.value); setDay(""); setSi(""); setTietIdx(""); }}
              style={formError && !classPick ? selErr : sel}
              disabled={!runId || state.myClasses.length === 0}
            >
              <option value="">— Lớp —</option>
              {state.myClasses.map((c) => (
                <option key={c.classId + "|" + c.subjectId} value={c.classId + "|" + c.subjectId}>{c.className} - {c.subjectName}</option>
              ))}
            </select>
            <select value={day} onChange={(e) => setDay(e.target.value)} style={formError && day === "" ? selErr : sel} disabled={!runId}>
              <option value="">— Thứ —</option>
              {DAYS.map((d, i) => (<option key={i} value={i}>{d}</option>))}
            </select>
            <select value={si} onChange={(e) => { setSi(e.target.value); setTietIdx(""); }} style={formError && si === "" ? selErr : sel} disabled={!runId || day === ""}>
              <option value="">— Buổi —</option>
              {sessions.map((s, i) => (<option key={i} value={i}>{s.label}</option>))}
            </select>
            <select value={tietIdx} onChange={(e) => setTietIdx(e.target.value)} style={formError && tietIdx === "" ? selErr : sel} disabled={!runId || si === ""}>
              <option value="">— Tiết —</option>
              {si !== "" && (sessions[Number(si)] || { periods: [] }).periods.map((p) => (
                <option key={p.idx} value={p.idx}>Tiết {p.idx + 1} ({p.time})</option>
              ))}
            </select>
            <button type="button" onClick={runSearch} disabled={!runId} style={primary}>🔍 Tìm kiếm</button>
          </div>
          {formError && <p style={{ color: "#dc2626", fontSize: 12, marginTop: 8, marginBottom: 0, fontWeight: 600 }}>⚠ {formError}</p>}
          {classPick && myClassSlots.length > 1 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
              <span style={{ fontSize: 12, color: "#6b7280", alignSelf: "center" }}>Lớp này có nhiều tiết trong tuần, chọn đúng buổi:</span>
              {myClassSlots.map((s) => {
                const active = Number(day) === s.day && Number(si) === s.si && Number(tietIdx) === s.tietIdx;
                return (
                  <button
                    key={s.global + "|" + s.day}
                    type="button"
                    onClick={() => { setDay(String(s.day)); setSi(String(s.si)); setTietIdx(String(s.tietIdx)); }}
                    style={active ? { ...chip, ...chipActive } : chip}
                  >
                    {DAYS[s.day]} · tiết {s.tietIdx + 1} ({s.time})
                  </button>
                );
              })}
            </div>
          )}
          {classPick && myClassSlots.length === 0 && (
            <p style={{ color: "#b45309", fontSize: 12, marginTop: 8, marginBottom: 0 }}>Lớp này chưa thấy tiết nào của bạn trong bản TKB đang xem.</p>
          )}
          {!runId && weekId && <p style={{ color: "#b45309", fontSize: 12, marginTop: 8, marginBottom: 0 }}>Tuần này chưa có bản thời khoá biểu nào xếp xong.</p>}
          {runId && slotLoading && <p style={{ color: "#6b7280", fontSize: 12, marginTop: 8, marginBottom: 0 }}>Đang tải TKB...</p>}
        </div>
      )}

      {!submitted ? null : combinedMatches.length === 0 ? (
        <div style={{ padding: "8px 9px", background: "#f9fafb", border: "1px solid #eef0f2", borderRadius: 6, fontSize: 13, color: "#6b7280" }}>
          Chưa tìm thấy giáo viên nào cùng bộ môn/cùng khối, hoặc dạy cùng lớp đang chọn.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {withBusy.map((t) => (
            <div key={t.staffId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "8px 10px", background: t.busy ? "#fef2f2" : "#f9fafb", border: `1px solid ${t.busy ? "#fee2e2" : "#eef0f2"}`, borderRadius: 8, opacity: t.busy ? 0.75 : 1 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    {t.name}
                    {slotChosen && (
                      t.busy
                        ? <span style={{ marginLeft: 8, fontSize: 11, color: "#dc2626", fontWeight: 700 }}>🔴 Đang bận</span>
                        : <span style={{ marginLeft: 8, fontSize: 11, color: "#059669", fontWeight: 700 }}>🟢 Trống tiết này</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>{t.matches.join(", ")}</div>
                </div>
                <a href={`tel:${t.phone}`} style={primary}>📞 {t.phone}</a>
              </div>
          ))}
        </div>
      )}
    </div>
  );
};

const primary = { padding: "8px 16px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, textDecoration: "none", whiteSpace: "nowrap" };
const sel = { padding: 8, border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, background: "#fff" };
const selErr = { ...sel, border: "1px solid #dc2626", background: "#fef2f2" };
const chip = { padding: "5px 10px", border: "1px solid #d1d5db", borderRadius: 999, fontSize: 12, background: "#fff", cursor: "pointer", color: "#374151" };
const chipActive = { background: "#0ea5a3", color: "#fff", borderColor: "#0ea5a3", fontWeight: 600 };

export default HappySchoolSubstituteTeachers;
